/* Vendor Quotation Detail — full view of a single quote + revision chain.
 *
 * Endpoint: GET /api/vendor-quotations/:id → { quotation, revisions[] }
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Truck, Package, Calendar, RefreshCw,
  Sparkles, History, ChevronRight, Edit, Printer, ShieldCheck,
  Percent, CreditCard, Hash,
} from 'lucide-react';


const statusTone = {
  draft:      'bg-gray-100 text-gray-700 border-gray-300',
  active:     'bg-emerald-100 text-emerald-800 border-emerald-300',
  superseded: 'bg-amber-100 text-amber-800 border-amber-300',
  expired:    'bg-rose-100 text-rose-700 border-rose-300',
};


export default function VendorQuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(id);

  const refresh = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/vendor-quotations/${selectedId}`);
      setData(r.data || null);
    } catch (_) {
      toast.error('Failed to load quotation');
      setData(null);
    }
  }, [selectedId]);

  const refreshResolution = async () => {
    try {
      const r = await axiosInstance.post(`/vendor-quotations/${selectedId}/refresh-resolution`);
      const b = r.data;
      if (b.newly_resolved > 0) {
        toast.success(`Resolved ${b.newly_resolved} line(s) · ${b.price_rows_written} price row(s) added · Item Master & RFQ now see this quote`);
      } else {
        toast.info('All lines already resolved — no changes');
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Refresh failed');
    }
  };

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setSelectedId(id); }, [id]);

  if (!data) {
    return <div className="p-6 text-sm text-gray-500" data-testid="vq-detail-loading">Loading quotation…</div>;
  }

  const q = data.quotation;
  const revisions = data.revisions || [];

  return (
    <div className="p-6 space-y-4" data-testid="vendor-quotation-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <button onClick={() => navigate('/vendor-quotations')}
                  className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mb-1"
                  data-testid="vq-back-button">
            <ArrowLeft className="w-3 h-3" /> Back to Quotations
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-3 flex-wrap">
            <FileText className="w-6 h-6 text-indigo-600" />
            <span className="font-mono" data-testid="vq-detail-no">{q.quote_no}</span>
            <span className={`text-xs px-2 py-0.5 rounded font-semibold border ${statusTone[q.status] || statusTone.draft}`}
                  data-testid="vq-detail-status">
              {q.status}
            </span>
            <span className="text-xs px-2 py-0.5 rounded bg-violet-100 text-violet-800 font-mono">
              V{q.revision_number}{q.is_latest && ' · LATEST'}
            </span>
          </h1>
          <div className="text-sm text-gray-600 mt-1 inline-flex items-center gap-2">
            <Truck className="w-4 h-4 text-gray-400" />
            {q.supplier_name || q.supplier_id}
            {q.supplier_quote_no && (
              <span className="ml-2 text-xs text-gray-500">
                · Supplier ref: <span className="font-mono">{q.supplier_quote_no}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh}
                  className="px-3 py-1.5 rounded text-xs border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          {q.lines?.some((l) => !l.item_code) && (
            <button onClick={refreshResolution}
                    data-testid="vq-refresh-resolution-button"
                    className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Resolve Items
            </button>
          )}
          {q.is_latest && q.status === 'active' && (
            <button onClick={() => navigate('/vendor-quotations', { state: { reviseId: q.id } })}
                    data-testid="vq-revise-button"
                    className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold inline-flex items-center gap-1">
              <Edit className="w-3 h-3" /> Revise (V+1)
            </button>
          )}
          <button onClick={() => window.print()}
                  className="px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-1">
            <Printer className="w-3 h-3" /> Print
          </button>
        </div>
      </div>

      {/* Header info grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3"
           data-testid="vq-detail-meta">
        <InfoCard icon={Calendar} label="Quote Date" value={q.quote_date} />
        <InfoCard icon={Calendar} label="Valid Until" value={q.valid_until || '—'}
                   tone={q.valid_until && new Date(q.valid_until) < new Date() ? 'rose' : 'default'} />
        <InfoCard icon={CreditCard} label="Payment Terms" value={q.payment_terms || '—'} />
        <InfoCard icon={Hash} label="Currency" value={q.currency || 'SAR'} />
      </div>

      {/* Lines table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-sm flex items-center justify-between">
          <span className="inline-flex items-center gap-2">
            <Package className="w-4 h-4" /> Quote Lines
            <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-900 text-white">{(q.lines || []).length}</span>
          </span>
        </div>
        <table className="w-full text-sm" data-testid="vq-detail-lines">
          {(() => {
            const hasInstall = (q.lines || []).some((ln) => ln.type_of_work === 'Supply & Installation');
            const totalCols  = hasInstall ? 12 : 11;
            return (
          <>
          <thead className="bg-gray-50 text-xs text-gray-600 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 w-12">#</th>
              <th className="text-left px-3 py-2">Type of Work</th>
              <th className="text-left px-3 py-2">Item Code</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">Size</th>
              <th className="text-left px-3 py-2">Supplier Description</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-left px-3 py-2">UOM</th>
              <th className="text-right px-3 py-2">Unit Price</th>
              <th className="text-right px-3 py-2">Delivery</th>
              {hasInstall && <th className="text-right px-3 py-2">Install Price</th>}
              <th className="text-right px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {(q.lines || []).map((l, i) => (
              <tr key={l.id || i} className="border-t border-gray-100"
                  data-testid={`vq-detail-line-${i}`}>
                <td className="px-3 py-2 text-xs text-gray-500 font-mono">{i + 1}</td>
                <td className="px-3 py-2 text-xs"
                    data-testid={`vq-detail-line-tow-${i}`}>
                  {l.type_of_work ? (
                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold border border-indigo-200">
                      {l.type_of_work}
                    </span>
                  ) : (
                    <span className="text-gray-400 italic">—</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{l.item_code || '—'}</td>
                <td className="px-3 py-2">
                  <div className="text-sm">{l.description}</div>
                  {l.notes && <div className="text-[10px] text-gray-500 mt-0.5">{l.notes}</div>}
                </td>
                <td className="px-3 py-2"
                    data-testid={`vq-detail-line-size-${i}`}>
                  <div className="text-sm text-gray-700 whitespace-pre-line">
                    {l.size || <span className="text-gray-400 italic">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2"
                    data-testid={`vq-detail-line-supplier-desc-${i}`}>
                  <div className="text-sm text-gray-700 whitespace-pre-line">
                    {l.supplier_description || <span className="text-gray-400 italic">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono">{l.quantity}</td>
                <td className="px-3 py-2 text-xs">{l.uom || 'ea'}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">
                  {Number(l.unit_price).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {l.lead_time_days != null ? `${l.lead_time_days}d` : '—'}
                </td>
                {hasInstall && (
                  <td className="px-3 py-2 text-right font-mono text-xs"
                      data-testid={`vq-detail-line-install-${i}`}>
                    {l.type_of_work === 'Supply & Installation' && l.install_price != null
                      ? Number(l.install_price).toLocaleString()
                      : <span className="text-gray-400 italic">—</span>}
                  </td>
                )}
                <td className="px-3 py-2 text-right font-mono font-semibold">
                  {Number(l.total || (l.quantity || 0) * (l.unit_price || 0)).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t border-gray-200 text-sm">
            <tr>
              <td colSpan={totalCols - 1} className="px-3 py-2 text-right text-gray-700">Subtotal</td>
              <td className="px-3 py-2 text-right font-mono font-semibold"
                  data-testid="vq-detail-subtotal">{Number(q.subtotal || 0).toLocaleString()}</td>
            </tr>
            {Number(q.general_discount_pct || 0) > 0 && (
              <tr>
                <td colSpan={totalCols - 1} className="px-3 py-2 text-right text-gray-700">
                  Discount ({q.general_discount_pct}%)
                </td>
                <td className="px-3 py-2 text-right font-mono text-rose-700"
                    data-testid="vq-detail-discount">−{Number(q.discount_amount || 0).toLocaleString()}</td>
              </tr>
            )}
            <tr className="border-t border-gray-300">
              <td colSpan={totalCols - 1} className="px-3 py-2 text-right font-bold">Grand Total</td>
              <td className="px-3 py-2 text-right font-mono font-bold text-indigo-700 text-base"
                  data-testid="vq-detail-grand-total">
                {Number(q.grand_total || 0).toLocaleString()} {q.currency || 'SAR'}
              </td>
            </tr>
          </tfoot>
          </>
            );
          })()}
        </table>
      </div>

      {/* Notes + Attachment */}
      {(q.notes || q.attachment_url) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {q.notes && (
            <div className="border border-gray-200 rounded-lg p-3 bg-amber-50/40">
              <div className="text-xs font-semibold text-gray-700 mb-1">Notes</div>
              <div className="text-sm text-gray-800 whitespace-pre-line">{q.notes}</div>
            </div>
          )}
          {q.attachment_url && (
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-700 mb-1">Attachment</div>
              <a href={q.attachment_url} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-indigo-600 hover:text-indigo-800 underline inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> Open PDF
              </a>
            </div>
          )}
        </div>
      )}

      {/* Revision Chain */}
      {revisions.length > 1 && (
        <div className="border border-violet-200 bg-violet-50 rounded-lg overflow-hidden"
             data-testid="vq-detail-revisions">
          <div className="px-3 py-2 bg-violet-100 border-b border-violet-300 font-semibold text-sm text-violet-900 flex items-center gap-2">
            <History className="w-4 h-4" /> Revision History
            <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-violet-900 text-white">{revisions.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-violet-900 bg-violet-50 border-b border-violet-200">
              <tr>
                <th className="text-left px-3 py-1">Version</th>
                <th className="text-left px-3 py-1">Quote No.</th>
                <th className="text-left px-3 py-1">Date</th>
                <th className="text-right px-3 py-1">Grand Total</th>
                <th className="text-left px-3 py-1">Status</th>
                <th className="text-left px-3 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {revisions.map((rev) => (
                <tr key={rev.id}
                    className={`border-t border-violet-200 ${rev.id === q.id ? 'bg-violet-100/50 font-semibold' : 'hover:bg-violet-50'} cursor-pointer`}
                    onClick={() => navigate(`/vendor-quotations/${rev.id}`)}
                    data-testid={`vq-revision-row-${rev.revision_number}`}>
                  <td className="px-3 py-1 font-mono text-xs">V{rev.revision_number}</td>
                  <td className="px-3 py-1 font-mono text-xs">{rev.quote_no}</td>
                  <td className="px-3 py-1 text-xs">{rev.quote_date}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">
                    {Number(rev.grand_total || 0).toLocaleString()} {rev.currency}
                  </td>
                  <td className="px-3 py-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${statusTone[rev.status] || statusTone.draft}`}>
                      {rev.status}
                    </span>
                  </td>
                  <td className="px-3 py-1 text-right">
                    {rev.id !== q.id && (
                      <ChevronRight className="w-3 h-3 text-violet-700 inline" />
                    )}
                    {rev.id === q.id && (
                      <span className="text-[10px] font-bold text-violet-700 inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> CURRENT
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit footer */}
      <div className="text-[11px] text-gray-400 inline-flex items-center gap-2">
        <Sparkles className="w-3 h-3" />
        Created {q.created_at?.slice(0, 16).replace('T', ' ')}
        {q.created_by_name && <span>by {q.created_by_name}</span>}
      </div>
    </div>
  );
}


function InfoCard({ icon: Icon, label, value, tone = 'default' }) {
  const toneClass = tone === 'rose'
    ? 'border-rose-200 bg-rose-50'
    : 'border-gray-200 bg-white';
  return (
    <div className={`border rounded-lg px-3 py-2 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
